import { GA4_MEASUREMENT_ID } from './tracking';

/**
 * GA4's identity for this browser, read from gtag itself.
 *
 * Needed because payment activation runs from a provider WEBHOOK with no
 * browser attached, so the server-sent `purchase` has nothing to attribute to.
 * GA4 requires `client_id` on web streams, and a fabricated one is worse than
 * no event at all: the purchase lands as a brand-new user with no prior
 * session, so GA4 books it to (direct)/(none) — revenue totals that are right
 * and channel attribution that is systematically wrong, which is the one thing
 * GA4 is actually good at.
 *
 * So it is captured HERE, at checkout, and persisted on the order — exactly as
 * `_fbp`/`_fbc` already are for Meta.
 *
 * Read through `gtag('get', …)` rather than by parsing the `_ga` cookie: the
 * accessor is the documented interface and survives cookie-format changes,
 * and it returns nothing when gtag never loaded, which is precisely the signal
 * we want (an ad-blocked buyer must yield NO id rather than a guess).
 */
async function gaField(field: 'client_id' | 'session_id'): Promise<string | undefined> {
  if (typeof gtag !== 'function') return undefined;
  return new Promise((resolve) => {
    // gtag never calls back when the tag failed to load, so the promise would
    // hang and block checkout. Analytics must never do that.
    const timer = setTimeout(() => resolve(undefined), GA_FIELD_TIMEOUT_MS);
    try {
      gtag('get', GA4_MEASUREMENT_ID, field, (value: unknown) => {
        clearTimeout(timer);
        resolve(typeof value === 'string' && value !== '' ? value : undefined);
      });
    } catch {
      clearTimeout(timer);
      resolve(undefined);
    }
  });
}

/** gtag is lazy-loaded on first interaction; a checkout click is an interaction,
 *  but the script may still be in flight. Short enough to be invisible. */
const GA_FIELD_TIMEOUT_MS = 300;

export interface GaContext {
  gaClientId?: string;
  gaSessionId?: string;
}

/**
 * Both GA4 identifiers, or an empty object when gtag is unavailable.
 *
 * `session_id` is what lets the server-sent purchase join the SESSION that
 * produced it, which is where session-scoped source/medium lives. Without it
 * only the user-scoped ("first user") dimensions resolve.
 */
export async function gaContext(): Promise<GaContext> {
  const [gaClientId, gaSessionId] = await Promise.all([
    gaField('client_id'),
    gaField('session_id'),
  ]);
  return { ...(gaClientId ? { gaClientId } : {}), ...(gaSessionId ? { gaSessionId } : {}) };
}
